"use client";

import { Brain, Target, HeartPulse, Lightbulb, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { PERSONALIZATION_AREAS } from "@/lib/constants";
import { AnimateOnScroll } from "@/components/shared/AnimateOnScroll";
import {
  StaggerContainer,
  StaggerItem,
} from "@/components/shared/StaggerContainer";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Brain, Target, HeartPulse, Lightbulb, Shield,
};

export function PersonalizationAreas() {
  return (
    <section className="relative overflow-hidden py-20 sm:py-28">
      {/* Gradient background */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary/[0.04] via-transparent to-transparent" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <AnimateOnScroll>
          <h2 className="text-center text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Personalized to five brain areas
          </h2>
        </AnimateOnScroll>
        <AnimateOnScroll delay={0.15}>
          <p className="mx-auto mt-5 max-w-2xl text-center text-xl text-muted-foreground sm:text-2xl">
            Take a quick quiz and get recommendations tailored to the areas that
            matter most to you.
          </p>
        </AnimateOnScroll>

        <StaggerContainer
          className="mx-auto mt-16 flex max-w-4xl flex-wrap justify-center gap-4"
          stagger={0.08}
        >
          {PERSONALIZATION_AREAS.map((area) => {
            const Icon = ICON_MAP[area.icon];
            return (
              <StaggerItem key={area.title} className="w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)]">
                <motion.div
                  whileHover={{ y: -4, scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                  className="h-full rounded-2xl border border-border/50 bg-card p-6 shadow-[var(--shadow-sm)] transition-shadow duration-300 hover:shadow-[var(--shadow-md)]"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary">
                    {Icon && <Icon className="h-5 w-5" />}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground">
                    {area.title}
                  </h3>
                  <p className="mt-1 text-base text-muted-foreground">
                    {area.description}
                  </p>
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
}
